import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "utils/api";
import { useAuth } from "context/AuthContext";
import Pagination from "components/pagination";
import { CSVLink } from "react-csv";
import { Search } from "lucide-react";

// Reusable Detail row
const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b py-1 text-sm">
    <span className="font-medium">{label}</span>
    <span>{value}</span>
  </div>
);

// Reusable Modal
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50">
    <div className="w-full max-w-md rounded-xl bg-white p-6">
      <h3 className="mb-4 text-lg font-bold">{title}</h3>
      {children}
      <button
        onClick={onClose}
        className="mt-4 w-full rounded-lg bg-green-400 py-2 hover:bg-green-600"
      >
        Close
      </button>
    </div>
  </div>
);

export default function OrdersTable() {
  const navigate = useNavigate();
  const { role, userId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [runners, setRunners] = useState([]);

  // Filters
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRunner, setFilterRunner] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const ORDERS_PAGE_SIZE = 10;
  const [ordersPage, setOrdersPage] = useState(1);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [runnerSearch, setRunnerSearch] = useState("");
  const [selectedRunner, setSelectedRunner] = useState(null);

  const filteredRunners = runners.filter((r) =>
    r.name.toLowerCase().includes(runnerSearch.toLowerCase())
  );

  const statusStyles = {
    Pending: "bg-yellow-100 text-yellow-800 border border-yellow-300",
    Confirmed: "bg-teal-100 text-teal-800 border border-teal-300",
    Assigned: "bg-blue-100 text-blue-800 border border-blue-300",
    "Picked Up": "bg-cyan-100 text-cyan-800 border border-cyan-300",
    "In Processing": "bg-purple-100 text-purple-800 border border-purple-300",
    "Out for Delivery":
      "bg-indigo-100 text-indigo-800 border border-indigo-300",
    Delivered: "bg-green-100 text-green-800 border border-green-300",
    "Cancelled by Customer": "bg-red-100 text-red-800 border border-red-300",
    Cancelled: "bg-red-100 text-red-800 border border-red-300",
    "Refund Processing": "bg-pink-100 text-pink-800 border border-pink-300",
    Refunded: "bg-gray-100 text-gray-600 border border-gray-300",
  };

  // Load orders from backend
  const loadOrders = async () => {
    try {
      const res = await api.get("/list_order");
      console.log("*******", res.data);
      const formatted = res.data.map((o) => ({
        id: o.id,
        ref_id: o.ref_id,
        userName: o.users_details?.name || o.user_details?.name,
        serviceType:
          o.booking_service_details?.[0]?.service_details?.service_name ||
          o.booking_service_details?.[0]?.service_details?.name,
        quantity: o.booking_service_details?.[0]?.product_count,
        pickupDate: o.pickup_date,
        pickupTime: o.pickup_time,
        dropDate: o.drop_date,
        dropTime: o.drop_time,
        deliveryDate: o.drop_date,
        pickupLocation: o.pickup_location,
        dropLocation: o.drop_location,
        status: o.status,
        runner: o.assigned_to || null,
      }));
      setOrders(formatted);
    } catch (err) {
      console.error("Order fetch failed", err);
    }
  };

  // Load runners from backend
  const loadRunners = async () => {
    try {
      const res = await api.post("/list_user", { role: "R" });
      const formatted = (res.data || []).map((r) => ({
        id: r.id,
        name: r.name,
        busy: false,
      }));
      setRunners(formatted);
    } catch (err) {
      console.error("Failed to load runners", err);
    }
  };

  useEffect(() => {
    loadOrders();
    if (role === "SA" || role === "A") {
      loadRunners();
    }
  }, [role]);

  const assignRunner = async (runner_id, runnerName, order_id) => {
    try {
      const payload = {
        order_id: order_id, //  matches backend
        runner_id: runner_id, //  matches backend
      };

      const response = await api.post("/assign_runner_to_order", payload);
      toast.success(response.data.message);

      // Update the orders array locally
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order_id
            ? { ...o, runner: runnerName, status: "Assigned" }
            : o
        )
      );

      // Reset modal state
      setShowAssignModal(false);
      setSelectedRunner(null);
      setIsDropdownOpen(false);
      setRunnerSearch("");
    } catch (err) {
      console.error("Assign failed:", err.response?.data || err);
      toast.error(err.response?.data?.detail || "Failed to assign runner");
    }
  };

  // Filtered orders
  const filteredOrders = orders.filter((o) => {
    const term = searchTerm.toLowerCase();
    const orderDate = new Date(o.pickupDate);
    const startDate = filterStartDate ? new Date(filterStartDate) : null;
    const endDate = filterEndDate ? new Date(filterEndDate) : null;
    const dateMatch =
      (!startDate || orderDate >= startDate) &&
      (!endDate || orderDate <= endDate);
    const statusMatch = filterStatus ? o.status === filterStatus : true;

    // Filter by runner ID
    const runnerMatch = filterRunner ? o.runner === filterRunner : true;

    const searchMatch =
      o.ref_id?.toLowerCase().includes(term) ||
      o.userName?.toLowerCase().includes(term) ||
      o.serviceType?.toLowerCase().includes(term) ||
      o.pickupLocation?.toLowerCase().includes(term) ||
      o.dropLocation?.toLowerCase().includes(term) ||
      o.status?.toLowerCase().includes(term);

    return dateMatch && statusMatch && runnerMatch && searchMatch;
  });

  const isAdmin = role === "SA" || role === "A";

  const runnerMap = new Map(runners.map((r) => [r.id, r.name]));

  const ordersTotalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / ORDERS_PAGE_SIZE)
  );
  const paginatedOrders = filteredOrders.slice(
    (ordersPage - 1) * ORDERS_PAGE_SIZE,
    ordersPage * ORDERS_PAGE_SIZE
  );

  useEffect(() => {
    setOrdersPage(1);
  }, [filterStartDate, filterEndDate, filterStatus, filterRunner, searchTerm]);
  const csvHeaders = [
    { label: "Order ID", key: "ref_id" },
    { label: "User", key: "userName" },
    { label: "Service", key: "serviceType" },
    { label: "Quantity", key: "quantity" },
    { label: "Pickup Date", key: "pickupDate" },
    { label: "Pickup Time", key: "pickupTime" },
    { label: "Pickup Location", key: "pickupLocation" },
    { label: "Drop Date", key: "dropDate" },
    { label: "Drop Time", key: "dropTime" },
    { label: "Drop Location", key: "dropLocation" },
    { label: "Status", key: "status" },
    { label: "Runner", key: "runner" },
  ];

  // Use filteredOrders (so export respects filters)
  const csvData = filteredOrders.map((o) => ({
    ref_id: o.ref_id,
    userName: o.userName,
    serviceType: o.serviceType,
    quantity: o.quantity,
    pickupDate: o.pickupDate,
    pickupTime: o.pickupTime,
    pickupLocation: o.pickupLocation,
    dropDate: o.dropDate,
    dropTime: o.dropTime,
    dropLocation: o.dropLocation,
    status: o.status,
    runner: o.runner ? runnerMap.get(o.runner) || o.runner : "Not Assigned",
  }));

  return (
    <div className="space-y-6 p-6">
      {/* Filters */}
      <div className="rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 hover:shadow-xl">
        <h3 className="mb-4 text-lg font-bold">Filter Orders</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Start Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">End Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            >
              <option value="">All</option>
              {Object.keys(statusStyles).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="mb-1 block text-sm font-medium">Runner</label>
              <select
                value={filterRunner}
                onChange={(e) => setFilterRunner(e.target.value)}
                className="w-full rounded border px-2 py-1 text-sm"
              >
                <option value="">All</option>
                {runners.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option> // Store the ID here for filtering
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 hover:shadow-xl">
        <div className="mb-4 flex flex-col items-center justify-between sm:flex-row">
          <h3 className="text-lg font-bold">
            {role === "R" ? "My Assigned Orders" : "Orders"}
          </h3>
          <div className="flex w-full items-center gap-3 sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                placeholder="Search order, user, service..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setOrdersPage(1);
                }}
                className="w-full rounded-lg border px-3 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>

            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`orders_${new Date().toISOString().slice(0, 10)}.csv`}
              className="border-slate-200 flex items-center gap-2 rounded-xl border bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 15V3" />
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="m7 10 5 5 5-5" />
              </svg>
              Export
            </CSVLink>
          </div>
        </div>

        <table className="w-full table-auto text-sm sm:table-fixed">
          <thead>
            <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="w-full px-2 py-3 sm:w-[15%]">Order ID</th>
              <th className="w-full px-2 py-3 sm:w-[12%]">User</th>
              <th className="w-full px-2 py-3 sm:w-[12%]">Service</th>
              <th className="w-full px-2 py-3 sm:w-[15%]">Pickup</th>
              <th className="w-full px-2 py-3 sm:w-[15%]">Drop</th>
              <th className="w-full px-2 py-3 text-center sm:w-[10%]">
                Status
              </th>
              {isAdmin && (
                <th className="w-full px-2 py-3 sm:w-[10%]">Runner</th>
              )}
              <th className="w-full px-2 py-3 text-center sm:w-[11%]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={isAdmin ? 8 : 7}
                  className="py-8 text-center text-gray-400"
                >
                  No orders found
                </td>
              </tr>
            ) : (
              filteredOrders.map((o) => (
                <tr
                  key={o.ref_id}
                  className="border-b align-middle hover:bg-gray-50"
                >
                  <td className="truncate px-2 py-2.5 font-medium text-gray-800">
                    {o.ref_id}
                  </td>
                  <td className="truncate px-2 py-2.5">{o.userName}</td>
                  <td className="truncate px-2 py-2.5">{o.serviceType}</td>
                  <td className="px-2 py-2.5">
                    <div className="text-gray-800">{o.pickupDate}</div>
                    <div className="text-xs text-gray-500">{o.pickupTime}</div>
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="text-gray-800">{o.dropDate}</div>
                    <div className="text-xs text-gray-500">{o.dropTime}</div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <span
                      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        statusStyles[o.status] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {o.status}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2.5">
                      {o.runner ? (
                        <span
                          className="block cursor-pointer truncate text-blue-600 hover:underline"
                          onClick={() =>
                            navigate(`/admin/runner_details/${o.runner}`)
                          }
                        >
                          {runnerMap.get(o.runner) || o.runner}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">
                          Not Assigned
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-2 py-2.5 text-center">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/admin/orders/${o.id}`)}
                        className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-200"
                      >
                        View
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setSelectedOrder(o);
                            const currentRunner = runners.find(
                              (r) => r.name === o.runner
                            );
                            setSelectedRunner(currentRunner || null);
                            setShowAssignModal(true);
                          }}
                          className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                            o.runner
                              ? "bg-yellow-500 text-white hover:bg-yellow-600"
                              : "bg-green-500 text-white hover:bg-green-600"
                          }`}
                        >
                          {o.runner ? "Re-Assign" : "Assign"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={ordersPage}
          totalPages={ordersTotalPages}
          totalItems={filteredOrders.length}
          pageSize={ORDERS_PAGE_SIZE}
          onPageChange={setOrdersPage}
          itemName="orders"
        />
      </div>

      {/* Assign Runner Modal */}
      {showAssignModal && selectedOrder && (
        <Modal
          title={`Assign Runner for Order #${selectedOrder.id}`}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedRunner(null);
            setRunnerSearch("");
            setIsDropdownOpen(false);
          }}
        >
          <div className="relative w-full">
            {/* Dropdown toggle */}
            <div
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex w-full cursor-pointer items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm"
            >
              <span>
                {selectedRunner
                  ? selectedRunner.name
                  : selectedOrder?.runner
                  ? runners.find((r) => r.id === selectedOrder.runner)?.name ||
                    "Unknown Runner"
                  : "Select Runner"}
              </span>

              {/* Show Assigned only if no new runner selected */}
              {selectedOrder?.runner && !selectedRunner && (
                <span className="text-xs font-medium text-green-600">
                  Assigned
                </span>
              )}
            </div>
            {/* Dropdown list */}
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border bg-white shadow-lg">
                {/* Search box */}
                <input
                  type="text"
                  placeholder="Search runner..."
                  value={runnerSearch}
                  onChange={(e) => setRunnerSearch(e.target.value)}
                  className="w-full border-b px-3 py-2 text-sm focus:outline-none"
                />

                {/* Runner list */}
                <div className="max-h-40 overflow-y-auto">
                  {filteredRunners.length > 0 ? (
                    filteredRunners.map((r) => (
                      <div
                        key={r.id}
                        onClick={() => {
                          setSelectedRunner(r);
                          setIsDropdownOpen(false);
                        }}
                        className={`cursor-pointer px-3 py-2 text-sm hover:bg-green-100 ${
                          selectedRunner?.id === r.id
                            ? "bg-green-200 font-medium"
                            : ""
                        }`}
                      >
                        {r.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No runners found
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assign button */}
            <button
              disabled={!selectedRunner}
              onClick={() => {
                if (selectedRunner) {
                  assignRunner(
                    selectedRunner.id,
                    selectedRunner.name,
                    selectedOrder.id
                  );
                  setIsDropdownOpen(false);
                }
              }}
              className={`mt-3 w-full rounded-lg py-2 text-white ${
                selectedRunner
                  ? "cursor-pointer bg-green-500 hover:bg-green-600"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              Assign Runner
            </button>
          </div>
        </Modal>
      )}

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <Modal title="Order Details" onClose={() => setShowDetailsModal(false)}>
          <Detail label="Order ID" value={selectedOrder.orderId} />
          <Detail label="User" value={selectedOrder.userName} />
          <Detail label="Service" value={selectedOrder.serviceType} />
          <Detail label="Qty" value={selectedOrder.quantity} />
          <Detail label="Pickup" value={selectedOrder.pickupDate} />
          <Detail label="Delivery" value={selectedOrder.deliveryDate} />
          <Detail
            label="Pickup Location"
            value={selectedOrder.pickupLocation}
          />
          <Detail label="Drop Location" value={selectedOrder.dropLocation} />
          <Detail label="Status" value={selectedOrder.status} />
          <Detail
            label="Runner"
            value={selectedOrder.runner ?? "Not Assigned"}
          />
        </Modal>
      )}
    </div>
  );
}
